import React, { useState, useMemo } from 'react';
import { MDBTable, MDBTableHead, MDBTableBody, MDBRow, MDBCol } from 'mdb-react-ui-kit';
import Button from './Button';

const DataTable = ({ 
    data = [], 
    columns = [], 
    itemsPerPage = 10, 
    className = '',
    tableProps = {},
    showPagination = true,
    showInfo = true 
}) => {
    const [currentPage, setCurrentPage] = useState(1);

    // Calculate pagination
    const totalPages = Math.ceil(data.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = useMemo(() => 
        data.slice(startIndex, endIndex), 
        [data, startIndex, endIndex]
    );

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // Previous button
        buttons.push(
            <Button
                key="prev"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                color="secondary"
                size="sm"
                className="me-1"
            >
                Previous
            </Button>
        );

        // Page number buttons
        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <Button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    color={currentPage === i ? "primary" : "secondary"}
                    size="sm"
                    className="me-1"
                >
                    {i}
                </Button>
            );
        }

        // Next button
        buttons.push(
            <Button
                key="next"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                color="secondary"
                size="sm"
                className="me-1"
            >
                Next
            </Button>
        );

        return buttons;
    };

    const renderCell = (item, column) => {
        if (column.render) {
            return column.render(item[column.key], item);
        }
        return item[column.key];
    };

    return (
        <div className={className}>
            <MDBTable align='middle' {...tableProps}>
                <MDBTableHead light>
                    <tr>
                        {columns.map((column) => (
                            <th key={column.key} scope='col' style={column.headerStyle}>
                                {column.title}
                            </th>
                        ))}
                    </tr>
                </MDBTableHead>
                <MDBTableBody>
                    {currentItems.map((item, index) => (
                        <tr key={item.id || index}>
                            {columns.map((column) => (
                                <td key={column.key} style={column.cellStyle}>
                                    {renderCell(item, column)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </MDBTableBody>
            </MDBTable>
            
            {showPagination && totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                    {showInfo && (
                        <div>
                            <small className="text-muted">
                                Showing {startIndex + 1} to {Math.min(endIndex, data.length)} of {data.length} entries
                            </small>
                        </div>
                    )}
                    <div>
                        {renderPaginationButtons()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataTable; 